import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useT } from "@/i18n";
import LangToggle from "@/components/LangToggle";
import PricingModal from "@/components/PricingModal";
import {
  ArrowUpRight, Gem, Flame, Watch, Mountain, Coins, ShieldCheck, FileText, Users, Target, Crown, Network,
  Atom, Wheat, Brain, Handshake, Landmark, ShieldAlert, LineChart, Calculator, Briefcase, Dumbbell,
  Award, BadgeCheck, TrendingUp, Lock, Zap, Sparkles, CheckCircle2,
} from "lucide-react";

const IMG = {
  hero: "https://static.prod-images.emergentagent.com/jobs/44e922b4-6326-4e9e-a61b-a036e60b36dc/images/b0deb49621af933b3aed96332141764082deadf8a5298848ef457b1b8ce944f3.png",
  or: "https://images.unsplash.com/photo-1755728806819-26a994fae81a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwzfHxnb2xkJTIwYmFycyUyMGluZ290cyUyMGRhcmt8ZW58MHx8fHwxNzc2Njc2MDA0fDA&ixlib=rb-4.1.0&q=85",
  diamonds: "https://static.prod-images.emergentagent.com/jobs/44e922b4-6326-4e9e-a61b-a036e60b36dc/images/ff3643cdc709a892584a22be741999c3ea8a0aa0e3cd31da5a5eacf26cfc3f4d.png",
  watches: "https://images.unsplash.com/photo-1768062251809-739d987a42fe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBtZWNoYW5pY2FsJTIwd2F0Y2glMjBtYWNyb3xlbnwwfHx8fDE3NzY2NzYwMDR8MA&ixlib=rb-4.1.0&q=85",
  oil: "https://images.unsplash.com/photo-1572093241294-2cb147be4281?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHw0fHxvaWwlMjByaWclMjBzdW5zZXQlMjBkYXJrJTIwc2lsaG91ZXR0ZXxlbnwwfHx8fDE3NzY2NzYwMDR8MA&ixlib=rb-4.1.0&q=85",
  cement: "https://static.prod-images.emergentagent.com/jobs/44e922b4-6326-4e9e-a61b-a036e60b36dc/images/ae77fbed94c451c4bb3872c7e764f6a1a7fe2bd85cd0bdd9365dad791ab2568c.png",
  metals: "https://images.unsplash.com/photo-1655549136009-6aa6ff5de57e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  soft: "https://images.unsplash.com/photo-1715302528444-f91e791ab592?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
};

// ============== HEADER ==============
const Header = () => {
  const { t } = useT();
  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#0A0A0A]/70 border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        <Link to="/" data-testid="brand-home" className="flex items-center gap-3 group">
          <div className="h-8 w-8 flex items-center justify-center border border-[#D4AF37]/70 text-[#D4AF37] font-serif italic text-sm">A</div>
          <div className="leading-none">
            <div className="font-serif text-base tracking-wide">AURUM</div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#A1A1AA]">Academy</div>
          </div>
        </Link>
        <nav className="hidden md:flex items-center gap-10 text-sm text-[#D4D4D8]">
          <a href="#promesse" className="gold-underline" data-testid="nav-promesse">{t("header.nav.promise")}</a>
          <a href="#secteurs" className="gold-underline" data-testid="nav-secteurs">{t("header.nav.sectors")}</a>
          <a href="#formation" className="gold-underline" data-testid="nav-formation">{t("header.nav.training")}</a>
          <a href="#strategie" className="gold-underline" data-testid="nav-strategie">{t("header.nav.strategy")}</a>
          <a href="#ressources" className="gold-underline" data-testid="nav-ressources">{t("header.nav.resources")}</a>
        </nav>
        <div className="flex items-center gap-3">
          <LangToggle testid="header-lang-toggle" />
          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event("aurum:open-pricing"))}
            data-testid="header-login-cta"
            className="group inline-flex items-center gap-2 px-4 py-2 border border-white/20 text-white text-sm hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
          >
            {t("common.access")} <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>
      </div>
    </header>
  );
};

// ============== HERO ==============
const Hero = () => {
  const { t } = useT();
  return (
    <section className="relative min-h-[100vh] pt-16 overflow-hidden">
      <div className="absolute inset-0">
        <img src={IMG.hero} alt="" className="w-full h-full object-cover opacity-[0.55]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/60 via-[#0A0A0A]/70 to-[#0A0A0A]" />
      </div>
      <div className="relative grain max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-8">
          <p data-testid="hero-eyebrow" className="fade-up text-[11px] uppercase tracking-[0.4em] text-[#D4AF37]">
            {t("hero.eyebrow")}
          </p>
          <h1
            data-testid="hero-title"
            className="fade-up d-1 font-serif text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.05] tracking-tight mt-6 font-light"
          >
            {t("hero.title.from")} <span className="text-[#D4AF37]">0</span> {t("hero.title.to")} <span className="text-[#D4AF37]">20 000 $</span> {t("hero.title.permonth")}
            <br /> {t("hero.title.signing")} <em className="italic text-[#F5F5DC]">{t("hero.title.contracts")}</em>.
          </h1>
          <p data-testid="hero-subtitle" className="fade-up d-2 mt-8 max-w-2xl text-base md:text-lg text-[#A1A1AA] leading-relaxed">
            {t("hero.subtitle")}
          </p>
          <div className="fade-up d-3 mt-10 flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={() => window.dispatchEvent(new Event("aurum:open-pricing"))}
              data-testid="hero-cta-access"
              className="group inline-flex items-center gap-3 px-7 py-4 bg-[#D4AF37] text-black text-sm uppercase tracking-[0.2em] hover:bg-[#F5F5DC] transition-colors"
            >
              {t("common.access_full")}
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <a
              href="#promesse"
              data-testid="hero-cta-discover"
              className="inline-flex items-center gap-2 px-7 py-4 border border-white/20 text-white text-sm uppercase tracking-[0.2em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
            >
              {t("common.see_program")}
            </a>
          </div>
          <div className="fade-up d-4 mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs text-[#A1A1AA]">
            <span className="flex items-center gap-2"><BadgeCheck className="h-4 w-4 text-[#D4AF37]" /> {t("hero.badge.method")}</span>
            <span className="flex items-center gap-2"><Award className="h-4 w-4 text-[#D4AF37]" /> {t("hero.badge.records")}</span>
            <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#D4AF37]" /> {t("hero.badge.deals")}</span>
          </div>
        </div>

        <div className="lg:col-span-4 lg:pl-10 lg:border-l lg:border-white/10 fade-up d-4">
          <div className="grid grid-cols-3 gap-6">
            <Stat value="07" label={t("hero.stat.sectors")} />
            <Stat value="11" label={t("hero.stat.modules")} />
            <Stat value="40+" label={t("hero.stat.suppliers")} />
          </div>
          <p className="mt-8 text-sm text-[#A1A1AA] leading-relaxed">
            {t("hero.note")} <span className="text-white">IMFPA, NCNDA, LOI</span>.
          </p>
        </div>
      </div>
    </section>
  );
};

const Stat = ({ value, label }) => (
  <div>
    <div className="font-serif text-4xl text-[#D4AF37]">{value}</div>
    <div className="mt-2 text-[10px] uppercase tracking-[0.28em] text-[#A1A1AA]">{label}</div>
  </div>
);

// ============== MARQUEE ==============
const Marquee = () => {
  const { t } = useT();
  const items = [
    t("marquee.gold"), t("marquee.diamonds"), t("marquee.watches"),
    t("marquee.oil"), t("marquee.metals"), t("marquee.soft"), t("marquee.docs"),
  ];
  return (
    <div className="border-y border-white/10 bg-[#0A0A0A] overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex items-center gap-8 flex-wrap text-[11px] uppercase tracking-[0.28em] text-[#A1A1AA]">
        {items.map((label, i) => (
          <React.Fragment key={i}>
            <span className="text-[#D4AF37]">•</span><span>{label}</span>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

// ============== PROMESSE ==============
const Promesse = () => {
  const { t } = useT();
  return (
    <section id="promesse" className="relative py-28 md:py-36 bg-[#0c0c0c] border-y border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 items-end">
          <div className="lg:col-span-7">
            <p className="text-[11px] uppercase tracking-[0.4em] text-[#D4AF37]">{t("promise.eyebrow")}</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light mt-4 leading-[1.1]">
              {t("promise.title1")} <em className="italic text-[#D4AF37]">{t("promise.title2")}</em>.<br />
              {t("promise.title3")} <span className="text-[#D4AF37]">100 %</span>.
            </h2>
          </div>
          <div className="lg:col-span-5 text-[#A1A1AA] leading-relaxed">
            {t("promise.intro")}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          <PillarCard icon={Target} num="01" title={t("promise.pillar1.title")} body={t("promise.pillar1.body")} />
          <PillarCard icon={Lock} num="02" title={t("promise.pillar2.title")} body={t("promise.pillar2.body")} />
          <PillarCard icon={TrendingUp} num="03" title={t("promise.pillar3.title")} body={t("promise.pillar3.body")} />
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-4 gap-5">
          <KPI value="0 → 20 k$" label={t("promise.kpi1.label")} />
          <KPI value={t("promise.kpi2.value")} label={t("promise.kpi2.label")} />
          <KPI value={t("promise.kpi3.value")} label={t("promise.kpi3.label")} />
          <KPI value={t("promise.kpi4.value")} label={t("promise.kpi4.label")} />
        </div>
      </div>
    </section>
  );
};

const PillarCard = ({ icon: Icon, num, title, body }) => (
  <div className="bg-[#0c0c0c] p-10 hover:bg-[#141414] transition-colors">
    <div className="flex items-center justify-between">
      <Icon className="h-6 w-6 text-[#D4AF37]" />
      <span className="text-[10px] uppercase tracking-[0.32em] text-[#A1A1AA]">{num} / 03</span>
    </div>
    <h3 className="font-serif text-2xl mt-8 leading-snug">{title}</h3>
    <p className="mt-4 text-[#A1A1AA] leading-relaxed text-sm">{body}</p>
  </div>
);

const KPI = ({ value, label }) => (
  <div className="border border-white/10 bg-[#0A0A0A] p-6">
    <div className="font-serif text-2xl md:text-3xl text-[#D4AF37]">{value}</div>
    <div className="mt-2 text-[10px] uppercase tracking-[0.28em] text-[#A1A1AA]">{label}</div>
  </div>
);

// ============== SECTEURS (7) ==============
const Sectors = () => {
  const { t } = useT();
  const items = [
    { id: "or", title: t("sectors.gold.title"), sub: t("sectors.gold.sub"), img: IMG.or, icon: Coins },
    { id: "petrole", title: t("sectors.oil.title"), sub: t("sectors.oil.sub"), img: IMG.oil, icon: Flame },
    { id: "pierres", title: t("sectors.gems.title"), sub: t("sectors.gems.sub"), img: IMG.diamonds, icon: Gem },
    { id: "horlogerie", title: t("sectors.watches.title"), sub: t("sectors.watches.sub"), img: IMG.watches, icon: Watch },
    { id: "ciment", title: t("sectors.cement.title"), sub: t("sectors.cement.sub"), img: IMG.cement, icon: Mountain },
    { id: "metaux", title: t("sectors.metals.title"), sub: t("sectors.metals.sub"), img: IMG.metals, icon: Atom },
    { id: "soft", title: t("sectors.soft.title"), sub: t("sectors.soft.sub"), img: IMG.soft, icon: Wheat },
  ];
  return (
    <section id="secteurs" className="relative py-28 md:py-36">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-6">
            <p className="text-[11px] uppercase tracking-[0.4em] text-[#D4AF37]">{t("sectors.eyebrow")}</p>
            <h2 className="font-serif text-4xl md:text-5xl font-light mt-4 leading-tight">
              {t("sectors.title1")}<br />{t("sectors.title2")}
            </h2>
          </div>
          <div className="lg:col-span-6 text-[#A1A1AA] leading-relaxed">
            {t("sectors.intro")}
          </div>
        </div>

        <div className="grid grid-cols-12 gap-4 md:gap-5">
          {items.map((it, idx) => {
            const Icon = it.icon;
            const span = idx === 0 ? "col-span-12 md:col-span-7 row-span-2 h-[360px] md:h-[520px]"
                        : idx === 1 ? "col-span-12 md:col-span-5 h-[250px]"
                        : idx === 2 ? "col-span-6 md:col-span-5 h-[250px]"
                        : idx === 3 ? "col-span-6 md:col-span-3 h-[250px]"
                        : idx === 4 ? "col-span-6 md:col-span-4 h-[250px]"
                        : idx === 5 ? "col-span-6 md:col-span-5 h-[250px]"
                        : "col-span-12 md:col-span-7 h-[250px]";
            return (
              <article
                key={it.id}
                data-testid={`sector-card-${it.id}`}
                className={`relative overflow-hidden group ${span} border border-white/10 bg-[#141414]`}
              >
                <img src={it.img} alt={it.title} className="absolute inset-0 h-full w-full object-cover opacity-60 group-hover:opacity-75 group-hover:scale-[1.03] transition-all duration-[900ms]" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
                <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-between">
                  <div className="flex items-center justify-between">
                    <Icon className="h-5 w-5 text-[#D4AF37]" />
                    <span className="text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA]">0{idx + 1}</span>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">{it.sub}</div>
                    <h3 className="font-serif text-2xl md:text-3xl mt-2">{it.title}</h3>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ============== FORMATION (7 modules métier) ==============
const Formation = () => {
  const { t } = useT();
  const modules = [
    { icon: Handshake, num: "01", title: t("formation.m1.title"), body: t("formation.m1.body") },
    { icon: Landmark, num: "02", title: t("formation.m2.title"), body: t("formation.m2.body") },
    { icon: ShieldAlert, num: "03", title: t("formation.m3.title"), body: t("formation.m3.body") },
    { icon: LineChart, num: "04", title: t("formation.m4.title"), body: t("formation.m4.body") },
    { icon: Calculator, num: "05", title: t("formation.m5.title"), body: t("formation.m5.body") },
    { icon: Briefcase, num: "06", title: t("formation.m6.title"), body: t("formation.m6.body") },
    { icon: Brain, num: "07", title: t("formation.m7.title"), body: t("formation.m7.body") },
  ];
  return (
    <section id="formation" className="relative py-28 md:py-36 bg-[#0a0a0a]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-6">
            <p className="text-[11px] uppercase tracking-[0.4em] text-[#D4AF37]">{t("formation.eyebrow")}</p>
            <h2 className="font-serif text-4xl md:text-5xl font-light mt-4 leading-tight">
              {t("formation.title1")}<br />{t("formation.title2")} <em className="italic text-[#D4AF37]">{t("formation.title3")}</em>.
            </h2>
          </div>
          <div className="lg:col-span-6 text-[#A1A1AA] leading-relaxed">
            {t("formation.intro")}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {modules.map((m) => (
            <ModuleCard key={m.num} {...m} totalDenom="07" availableLabel={t("strategy.card.available")} />
          ))}
        </div>
      </div>
    </section>
  );
};

// ============== STRATÉGIE (4 modules) ==============
const Strategie = () => {
  const { t } = useT();
  const modules = [
    { icon: Target, num: "01", title: t("strategy.s1.title"), body: t("strategy.s1.body") },
    { icon: Crown, num: "02", title: t("strategy.s2.title"), body: t("strategy.s2.body") },
    { icon: Network, num: "03", title: t("strategy.s3.title"), body: t("strategy.s3.body") },
    { icon: Zap, num: "04", title: t("strategy.s4.title"), body: t("strategy.s4.body") },
  ];
  return (
    <section id="strategie" className="relative py-28 md:py-36 bg-[#0c0c0c] border-y border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-6">
            <p className="text-[11px] uppercase tracking-[0.4em] text-[#D4AF37]">{t("strategy.eyebrow")}</p>
            <h2 className="font-serif text-4xl md:text-5xl font-light mt-4 leading-tight">
              {t("strategy.title1")}<br />
              {t("strategy.title2")} <em className="italic text-[#D4AF37]">{t("strategy.title3")}</em> {t("strategy.title4")}
            </h2>
          </div>
          <div className="lg:col-span-6 text-[#A1A1AA] leading-relaxed">
            {t("strategy.intro")}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {modules.map((m) => (
            <ModuleCard key={m.num} {...m} totalDenom="04" availableLabel={t("strategy.card.available")} />
          ))}
        </div>
      </div>
    </section>
  );
};

const ModuleCard = ({ icon: Icon, num, title, body, totalDenom, availableLabel }) => (
  <article className="relative border border-white/10 bg-[#0c0c0c] p-7 md:p-9 hover:border-[#D4AF37]/40 transition-colors group">
    <div className="flex items-center justify-between">
      <Icon className="h-5 w-5 text-[#D4AF37]" />
      <span className="text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA]">{num} / {totalDenom}</span>
    </div>
    <h3 className="font-serif text-xl md:text-2xl mt-7 leading-snug">{title}</h3>
    <p className="mt-4 text-sm text-[#A1A1AA] leading-relaxed">{body}</p>
    <div className="mt-7 pt-5 border-t border-white/10 flex items-center justify-between text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA] group-hover:text-[#D4AF37] transition-colors">
      <span>{availableLabel}</span>
      <ArrowUpRight className="h-3.5 w-3.5" />
    </div>
  </article>
);

// ============== EXERCICES PRATIQUES ==============
const Exercices = () => {
  const { t } = useT();
  return (
    <section id="exercices" className="relative py-28 md:py-36 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <p className="text-[11px] uppercase tracking-[0.4em] text-[#D4AF37]">{t("exercises.eyebrow")}</p>
          <h2 className="font-serif text-4xl md:text-5xl font-light mt-4 leading-tight">
            {t("exercises.title1")}<br /><em className="italic text-[#D4AF37]">{t("exercises.title2")}</em>.
          </h2>
          <p className="mt-6 text-[#A1A1AA] leading-relaxed">
            {t("exercises.intro1")}
          </p>
          <p className="mt-4 text-[#A1A1AA] leading-relaxed">
            {t("exercises.intro2")}
          </p>
        </div>
        <div className="lg:col-span-7">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <ExerciceCard n="01" label={t("exercises.card_label")} title={t("exercises.card1.title")} body={t("exercises.card1.body")} />
            <ExerciceCard n="02" label={t("exercises.card_label")} title={t("exercises.card2.title")} body={t("exercises.card2.body")} />
          </div>
          <div className="mt-6 border border-[#D4AF37]/30 bg-[#0c0c0c] p-6 flex items-start gap-4">
            <Sparkles className="h-5 w-5 text-[#D4AF37] shrink-0 mt-0.5" />
            <p className="text-sm text-[#A1A1AA] leading-relaxed">
              <span className="text-white">{t("exercises.coming_soon_label")}</span> {t("exercises.coming_soon")}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const ExerciceCard = ({ n, label, title, body }) => (
  <div className="border border-[#D4AF37]/30 bg-[#0c0c0c] p-7 hover:border-[#D4AF37]/60 transition-colors">
    <div className="flex items-center justify-between">
      <div className="h-10 w-10 border border-[#D4AF37]/50 bg-[#D4AF37]/5 flex items-center justify-center text-[#D4AF37]">
        <Dumbbell className="h-4 w-4" />
      </div>
      <span className="text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA]">{label} · {n}</span>
    </div>
    <h3 className="font-serif text-xl mt-6 leading-snug">{title}</h3>
    <p className="mt-3 text-sm text-[#A1A1AA] leading-relaxed">{body}</p>
  </div>
);

// ============== RESSOURCES ==============
const Ressources = () => {
  const { t } = useT();
  const rows = [
    ["NDA", t("resources.row.nda")],
    ["NCNDA", t("resources.row.ncnda")],
    ["LOI", t("resources.row.loi")],
    ["SPA", t("resources.row.spa")],
    ["SCO", t("resources.row.sco")],
    ["FCO", t("resources.row.fco")],
    ["Spec Sheet EN590", t("resources.row.spec")],
    ["Company Profile", t("resources.row.cp")],
    ["GIA Certificate", t("resources.row.gia")],
  ];
  return (
    <section id="ressources" className="relative py-28 md:py-36">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <p className="text-[11px] uppercase tracking-[0.4em] text-[#D4AF37]">{t("resources.eyebrow")}</p>
          <h2 className="font-serif text-4xl md:text-5xl font-light mt-4 leading-tight">
            {t("resources.title1")}<br />{t("resources.title2")} <em className="italic text-[#D4AF37]">{t("resources.title3")}</em>.
          </h2>
          <p className="mt-6 text-[#A1A1AA] leading-relaxed">
            {t("resources.intro")}
          </p>
        </div>
        <div className="lg:col-span-7">
          <ul className="divide-y divide-white/10 border-y border-white/10">
            {rows.map(([code, desc], i) => (
              <li key={code} data-testid={`resource-item-${code.toLowerCase().replace(/\s+/g,'-')}`} className="py-5 flex items-center justify-between group">
                <div className="flex items-baseline gap-6">
                  <span className="font-serif text-lg text-[#D4AF37] w-10">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <div className="font-serif text-lg">{code}</div>
                    <div className="text-sm text-[#A1A1AA] mt-1">{desc}</div>
                  </div>
                </div>
                <span className="text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA] group-hover:text-[#D4AF37] transition-colors">
                  {t("resources.row.required")}
                </span>
              </li>
            ))}
          </ul>
          <div className="mt-8 grid grid-cols-2 gap-4">
            <div className="border border-white/10 bg-[#0c0c0c] p-5">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">{t("resources.kpi.suppliers.label")}</div>
              <div className="font-serif text-3xl mt-3 text-white">40 +</div>
              <div className="text-xs text-[#A1A1AA] mt-2">{t("resources.kpi.suppliers.sub")}</div>
            </div>
            <div className="border border-white/10 bg-[#0c0c0c] p-5">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">{t("resources.kpi.docs.label")}</div>
              <div className="font-serif text-3xl mt-3 text-white">09</div>
              <div className="text-xs text-[#A1A1AA] mt-2">{t("resources.kpi.docs.sub")}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============== POURQUOI AURUM ==============
const Pourquoi = () => {
  const { t } = useT();
  return (
    <section className="relative py-28 md:py-36 bg-[#0c0c0c] border-y border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl">
          <p className="text-[11px] uppercase tracking-[0.4em] text-[#D4AF37]">{t("why.eyebrow")}</p>
          <h2 className="font-serif text-4xl md:text-5xl font-light mt-4 leading-tight">
            {t("why.title1")} <em className="italic text-[#D4AF37]">{t("why.title2")}</em>,
            <br />{t("why.title3")}
          </h2>
          <p className="mt-6 text-[#A1A1AA] leading-relaxed">
            {t("why.intro")}
          </p>
        </div>
        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-5">
          <Argument icon={BadgeCheck} title={t("why.arg1.title")} body={t("why.arg1.body")} />
          <Argument icon={ShieldCheck} title={t("why.arg2.title")} body={t("why.arg2.body")} />
          <Argument icon={Lock} title={t("why.arg3.title")} body={t("why.arg3.body")} />
          <Argument icon={TrendingUp} title={t("why.arg4.title")} body={t("why.arg4.body")} />
        </div>
      </div>
    </section>
  );
};

const Argument = ({ icon: Icon, title, body }) => (
  <div className="border border-white/10 bg-[#0A0A0A] p-6">
    <Icon className="h-5 w-5 text-[#D4AF37]" />
    <h3 className="font-serif text-lg mt-5">{title}</h3>
    <p className="mt-3 text-xs text-[#A1A1AA] leading-relaxed">{body}</p>
  </div>
);

// ============== PRICING ==============
const Pricing = () => {
  const { t } = useT();
  const features = [
    "pricing.feature.1",
    "pricing.feature.2",
    "pricing.feature.3",
    "pricing.feature.4",
    "pricing.feature.5",
  ];
  return (
    <section id="pricing" className="relative py-28 md:py-36 bg-[#0A0A0A] overflow-hidden">
      <div className="absolute -left-32 top-1/2 -translate-y-1/2 h-[420px] w-[420px] rounded-full blur-3xl opacity-[0.18] bg-[#D4AF37]" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <p className="text-[11px] uppercase tracking-[0.4em] text-[#D4AF37]">{t("pricing.eyebrow")}</p>
          <h2 className="font-serif text-4xl md:text-5xl font-light mt-5 leading-[1.05]">
            {t("pricing.title1")} <em className="italic text-[#D4AF37]">{t("pricing.title2")}</em> {t("pricing.title3")}.
          </h2>
          <p className="mt-7 text-[#A1A1AA] leading-relaxed max-w-md">{t("pricing.intro")}</p>
        </div>

        <div className="lg:col-span-7">
          <div data-testid="pricing-card" className="relative border border-[#D4AF37]/40 bg-gradient-to-br from-[#141414] to-[#0c0c0c] p-8 md:p-12">
            <div className="absolute top-0 right-0 bg-[#D4AF37] text-black text-[10px] uppercase tracking-[0.32em] px-4 py-2">
              {t("pricing.plan.tag")}
            </div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#A1A1AA]">{t("pricing.plan.name")}</div>
            <div className="mt-6 flex items-baseline gap-2">
              <span className="font-serif text-7xl md:text-8xl text-[#D4AF37] tabular-nums leading-none">{t("pricing.plan.price")}</span>
              <span className="font-serif text-3xl text-[#D4AF37]">{t("pricing.plan.currency")}</span>
            </div>

            <ul className="mt-10 space-y-3">
              {features.map((k) => (
                <li key={k} className="flex items-start gap-3 text-sm text-[#E4E4E7]">
                  <CheckCircle2 className="h-4 w-4 text-[#D4AF37] shrink-0 mt-0.5" />
                  <span>{t(k)}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 pt-8 border-t border-white/10 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
              <p className="text-[10px] uppercase tracking-[0.28em] text-[#A1A1AA] max-w-[260px]">{t("pricing.note")}</p>
              <button
                type="button"
                onClick={() => window.dispatchEvent(new Event("aurum:open-pricing"))}
                data-testid="pricing-cta"
                className="inline-flex items-center gap-3 px-7 py-4 bg-[#D4AF37] text-black text-sm uppercase tracking-[0.22em] hover:bg-[#F5F5DC] transition-colors whitespace-nowrap"
              >
                {t("pricing.cta")} <ArrowUpRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============== FINAL CTA ==============
const FinalCTA = () => {
  const { t } = useT();
  return (
    <section id="contact" className="relative py-28 md:py-36 bg-[#0A0A0A] overflow-hidden">
      <div className="absolute -right-32 -top-32 h-[480px] w-[480px] rounded-full blur-3xl opacity-20 bg-[#D4AF37]" />
      <div className="relative max-w-4xl mx-auto px-6 md:px-10 text-center">
        <p className="text-[11px] uppercase tracking-[0.4em] text-[#D4AF37]">{t("cta.eyebrow")}</p>
        <h2 className="font-serif text-4xl md:text-6xl font-light mt-6 leading-[1.05]">
          {t("cta.title1")}<br />{t("cta.title2")} <em className="italic text-[#D4AF37]">{t("cta.title3")}</em> ?
        </h2>
        <p className="mt-8 text-[#A1A1AA] max-w-xl mx-auto leading-relaxed">
          {t("cta.body")}
        </p>
        <div className="mt-12">
          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event("aurum:open-pricing"))}
            data-testid="final-cta-access"
            className="inline-flex items-center gap-3 px-10 py-5 bg-[#D4AF37] text-black text-sm uppercase tracking-[0.25em] hover:bg-[#F5F5DC] transition-colors"
          >
            {t("cta.button")}
            <ArrowUpRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

// ============== FOOTER ==============
const Footer = () => {
  const { t } = useT();
  return (
    <footer className="border-t border-white/10 py-10">
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-[#A1A1AA]">
        <div className="flex items-center gap-3">
          <div className="h-6 w-6 flex items-center justify-center border border-[#D4AF37]/60 text-[#D4AF37] font-serif italic text-xs">A</div>
          <span className="font-serif">AURUM Academy</span>
        </div>
        <div className="uppercase tracking-[0.28em]">{t("common.year_copyright")}</div>
      </div>
    </footer>
  );
};

export default function Landing() {
  const { lang } = useT();
  const [pricingOpen, setPricingOpen] = useState(false);
  useEffect(() => {
    document.title = lang === "en"
      ? "AURUM Academy · Brokerage program"
      : "AURUM Academy · Formation en intermédiation";
  }, [lang]);
  useEffect(() => {
    const open = () => setPricingOpen(true);
    window.addEventListener("aurum:open-pricing", open);
    return () => window.removeEventListener("aurum:open-pricing", open);
  }, []);
  return (
    <div data-testid="landing-page" className="relative">
      <Header />
      <Hero />
      <Marquee />
      <Promesse />
      <Sectors />
      <Formation />
      <Strategie />
      <Exercices />
      <Ressources />
      <Pourquoi />
      <Pricing />
      <FinalCTA />
      <Footer />
      <PricingModal open={pricingOpen} onClose={() => setPricingOpen(false)} />
    </div>
  );
}
